import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, ImageIcon, Loader2, Eye, Pencil } from 'lucide-react';
import { RichTextEditor } from '@/components/admin/blog/RichTextEditor';
import { slugify } from '@/lib/blog/markdownConvert';
import { renderMarkdown } from '@/lib/blogCms';
import { sanitizeHtml } from '@/lib/sanitizeHtml';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  category: string;
  read_time: string;
  excerpt: string;
  body_markdown: string;
  cover_image_url: string | null;
  seo_title: string | null;
  meta_description: string | null;
  status: 'draft' | 'scheduled' | 'published' | 'archived';
  published_at: string | null;
  scheduled_for: string | null;
  is_featured: boolean;
  author_name: string | null;
}

const CATEGORIES = ['Guide', 'Playbook', 'Comparison', 'Tutorial', 'Opinion', 'Industry Insights', 'Product Update'] as const;

const EXCERPT_LIMIT = 160;
const META_LIMIT = 160;

const EMPTY: Partial<BlogPost> = {
  slug: '',
  title: '',
  category: 'Guide',
  read_time: '5 min',
  excerpt: '',
  body_markdown: '',
  cover_image_url: null,
  seo_title: '',
  meta_description: '',
  status: 'draft',
  published_at: null,
  scheduled_for: null,
  is_featured: false,
  author_name: 'Sorvaine Team',
};

// Avg adult reading: ~225 wpm. Strip markdown/HTML noise for accurate counts.
function computeStats(md: string): { words: number; readMin: number } {
  const text = (md || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]+\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[#*_>`~\-]/g, ' ')
    .replace(/<[^>]+>/g, ' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const readMin = Math.max(1, Math.round(words / 225));
  return { words, readMin };
}

export default function AdminBlogEditor() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const draftKey = `blog-draft:${id || 'new'}`;

  const [post, setPost] = useState<Partial<BlogPost>>({ ...EMPTY });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [slugDirty, setSlugDirty] = useState(isEdit);
  const [preview, setPreview] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  const debounceRef = useRef<number | null>(null);

  // Load post (and recover any newer local draft)
  useEffect(() => {
    if (!id) {
      // New post: try recover unsaved draft
      try {
        const raw = sessionStorage.getItem(draftKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed?.title || parsed?.body_markdown) {
            setPost((p) => ({ ...p, ...parsed }));
            setSlugDirty(true);
            toast.message('Recovered unsaved draft');
          }
        }
      } catch {}
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from('blog_posts').select('*').eq('id', id).maybeSingle();
      if (cancelled) return;
      if (error) { toast.error(error.message); navigate('/admin/blog'); return; }
      if (!data) { toast.error('Post not found'); navigate('/admin/blog'); return; }
      setPost(data as BlogPost);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, navigate, draftKey]);

  // Debounced autosave to sessionStorage (1.5s)
  useEffect(() => {
    if (loading) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      try {
        sessionStorage.setItem(draftKey, JSON.stringify(post));
        setDraftSavedAt(new Date());
      } catch {}
    }, 1500);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [post, draftKey, loading]);

  const setField = <K extends keyof BlogPost>(key: K, value: BlogPost[K]) =>
    setPost((p) => ({ ...p, [key]: value }));

  const handleTitleChange = (title: string) => {
    setPost((p) => ({ ...p, title, slug: slugDirty ? p.slug : slugify(title) }));
  };

  const handleSlugChange = (slug: string) => {
    setSlugDirty(true);
    setField('slug', slugify(slug));
  };

  const handleImageUpload = async (file: File) => {
    setUploading(true);
    const ext = file.name.split('.').pop();
    const path = `${Date.now()}-${slugify(post.slug || 'cover')}.${ext}`;
    const { error: upErr } = await supabase.storage.from('blog-images').upload(path, file, { upsert: false });
    if (upErr) { toast.error(upErr.message); setUploading(false); return; }
    const { data } = supabase.storage.from('blog-images').getPublicUrl(path);
    setField('cover_image_url', data.publicUrl);
    setUploading(false);
    toast.success('Image uploaded');
  };

  const handleSave = async () => {
    if (!post.title?.trim() || !post.slug?.trim()) {
      toast.error('Title and slug are required');
      return;
    }
    setSaving(true);
    const payload = {
      slug: post.slug,
      title: post.title,
      category: post.category || 'Guide',
      read_time: post.read_time || '5 min',
      excerpt: post.excerpt || '',
      body_markdown: post.body_markdown || '',
      cover_image_url: post.cover_image_url || null,
      seo_title: post.seo_title || null,
      meta_description: post.meta_description || null,
      status: post.status || 'draft',
      published_at: post.published_at || null,
      scheduled_for: post.scheduled_for || null,
      is_featured: !!post.is_featured,
      author_name: post.author_name || null,
    };

    let error;
    if (isEdit && id) {
      ({ error } = await supabase.from('blog_posts').update(payload).eq('id', id));
    } else {
      const { data: { user } } = await supabase.auth.getUser();
      ({ error } = await supabase.from('blog_posts').insert([{ ...payload, created_by: user?.id }] as never));
    }
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    try { sessionStorage.removeItem(draftKey); } catch {}
    toast.success(isEdit ? 'Post updated' : 'Post created');
    navigate('/admin/blog');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-7 w-48 bg-muted rounded animate-pulse" />
        <div className="h-10 bg-muted rounded animate-pulse" />
        <div className="h-[560px] bg-muted rounded animate-pulse" />
      </div>
    );
  }

  const isLive = post.status === 'published' && (!post.published_at || new Date(post.published_at) <= new Date());
  const stats = computeStats(post.body_markdown || '');
  const suggestedReadTime = `${stats.readMin} min`;
  const readTimeMatches = (post.read_time || '').trim() === suggestedReadTime;

  const excerptLen = (post.excerpt || '').length;
  const metaLen = (post.meta_description || '').length;

  return (
    <div>
      {/* Header strip */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/blog" className="gap-1.5">
              <ArrowLeft className="w-4 h-4" /> Back to Blog CMS
            </Link>
          </Button>
          {draftSavedAt && (
            <span className="text-[11px] text-muted-foreground">
              Draft saved locally · {draftSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPreview((v) => !v)} className="gap-1.5">
            {preview ? <><Pencil className="w-3.5 h-3.5" /> Edit</> : <><Eye className="w-3.5 h-3.5" /> Preview</>}
          </Button>
          {isEdit && isLive && post.slug && (
            <Button variant="outline" size="sm" asChild>
              <a href={`/resources/${post.slug}`} target="_blank" rel="noreferrer" className="gap-1.5">
                <Eye className="w-3.5 h-3.5" /> View live
              </a>
            </Button>
          )}
          <Button variant="ghost" onClick={() => navigate('/admin/blog')}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            {isEdit ? 'Save changes' : 'Create post'}
          </Button>
        </div>
      </div>

      <h1 className="text-2xl font-semibold mb-1">{isEdit ? 'Edit post' : 'New post'}</h1>
      <p className="text-sm text-muted-foreground mb-8">
        Public visitors only see posts marked <strong>Published</strong> with a publish date in the past.
      </p>

      {preview ? (
        <div className="rounded-md border border-border bg-white p-8 md:p-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-primary/80">{post.category || 'Guide'}</span>
            <span className="text-[11px] text-gray-400">{post.read_time || suggestedReadTime}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1]">
            {post.title || 'Untitled'}
          </h1>
          {post.excerpt && <p className="mt-4 text-lg text-gray-500 max-w-[620px]">{post.excerpt}</p>}
          {post.cover_image_url && (
            <img src={post.cover_image_url} alt={post.title || ''} className="mt-8 w-full h-auto rounded-xl" />
          )}
          <div
            className="mt-10 text-gray-500 leading-relaxed prose prose-sm max-w-none prose-strong:text-gray-700 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-ul:mt-3 prose-li:mt-1 prose-headings:text-gray-900 prose-p:text-gray-500 prose-img:w-full prose-img:h-auto prose-img:rounded-xl"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderMarkdown(post.body_markdown || '')) }}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          {/* Main column */}
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={post.title || ''} onChange={(e) => handleTitleChange(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Slug (URL)</Label>
                <Input value={post.slug || ''} onChange={(e) => handleSlugChange(e.target.value)} className="mt-1 font-mono text-xs" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={post.category || 'Guide'} onValueChange={(v) => setField('category', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Read time</Label>
                <div className="flex items-center gap-1.5 mt-1">
                  <Input value={post.read_time || ''} onChange={(e) => setField('read_time', e.target.value)} />
                  {!readTimeMatches && stats.words > 50 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-9 px-2 text-[11px] whitespace-nowrap text-primary"
                      onClick={() => setField('read_time', suggestedReadTime)}
                      title="Use suggested read time"
                    >
                      Use {suggestedReadTime}
                    </Button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">{stats.words} words · ~{stats.readMin} min</p>
              </div>
              <div>
                <Label className="text-xs">Author</Label>
                <Input value={post.author_name || ''} onChange={(e) => setField('author_name', e.target.value)} className="mt-1" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Excerpt</Label>
                <span className={`text-[10px] tabular-nums ${excerptLen > EXCERPT_LIMIT ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {excerptLen}/{EXCERPT_LIMIT}
                </span>
              </div>
              <Textarea
                rows={2}
                value={post.excerpt || ''}
                onChange={(e) => setField('excerpt', e.target.value)}
                className="mt-1 resize-none"
                placeholder="One or two sentences shown on the blog index card."
              />
            </div>

            <div>
              <Label className="text-xs">Body</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={post.body_markdown || ''}
                  onChange={(md) => setField('body_markdown', md)}
                  placeholder="Write your post… use the toolbar for headings, lists, links."
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-xs">SEO title (optional)</Label>
                <Input value={post.seo_title || ''} onChange={(e) => setField('seo_title', e.target.value)} className="mt-1" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Meta description (optional)</Label>
                  <span className={`text-[10px] tabular-nums ${metaLen > META_LIMIT ? 'text-destructive' : 'text-muted-foreground'}`}>
                    {metaLen}/{META_LIMIT}
                  </span>
                </div>
                <Textarea
                  rows={3}
                  maxLength={200}
                  value={post.meta_description || ''}
                  onChange={(e) => setField('meta_description', e.target.value)}
                  className="mt-1 resize-none"
                  placeholder="Up to 160 characters shown in search results."
                />
              </div>
            </div>
          </div>

          {/* Sidebar column */}
          <aside className="space-y-6">
            <div className="rounded-md border border-border bg-card p-4 space-y-4">
              <div>
                <Label className="text-xs">Status</Label>
                <Select
                  value={post.status || 'draft'}
                  onValueChange={(v) => setField('status', v as BlogPost['status'])}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft (hidden)</SelectItem>
                    <SelectItem value="published">Published (live)</SelectItem>
                    <SelectItem value="archived">Archived (hidden)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Publish date (UTC)</Label>
                <Input
                  type="datetime-local"
                  className="mt-1"
                  value={post.published_at ? new Date(post.published_at).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setField('published_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
                />
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  Set a future date with status <strong>Published</strong> to schedule.
                </p>
              </div>

              <div className="flex items-center justify-between pt-1">
                <Label className="text-xs">Featured on blog index</Label>
                <Switch
                  checked={!!post.is_featured}
                  onCheckedChange={(c) => setField('is_featured', c)}
                />
              </div>
            </div>

            <div className="rounded-md border border-border bg-card p-4 space-y-3">
              <Label className="text-xs">Cover image</Label>
              {post.cover_image_url ? (
                <img src={post.cover_image_url} alt="" className="w-full h-32 object-cover rounded border border-border" />
              ) : (
                <div className="w-full h-32 rounded border border-dashed border-border flex items-center justify-center text-muted-foreground">
                  <ImageIcon className="w-6 h-6" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
                  className="hidden"
                  id="cover-upload"
                />
                <Button asChild type="button" variant="outline" size="sm" disabled={uploading} className="flex-1">
                  <label htmlFor="cover-upload" className="cursor-pointer gap-1.5">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                    {post.cover_image_url ? 'Replace' : 'Upload'}
                  </label>
                </Button>
                {post.cover_image_url && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setField('cover_image_url', null)}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
